import styles from "./UserInfo.module.css";
import defaultAvatar from "../../assets/images/avatar_default.png";
import { FaChevronRight } from "react-icons/fa";
import {
  selectSelectedServiceId,
  selectUser,
} from "../../redux/auth/selectors";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import Modal from "../Modals/Modal/Modal.jsx";
import PaymentTopUpAccountModal from "../Modals/PaymentTopUpAccountModal/PaymentTopUpAccountModal.jsx";
import { selectIsChatOpen } from "../../redux/chat/selectors.js";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { getPosts } from "../../redux/settings/operations.js";
import {
  getBalanceUsageDetails,
  getTariffs,
} from "../../redux/tariffs/operations.js";
import useTooltip from "../../utils/useTooltip.jsx";
import Tooltip from "../sharedComponents/Tooltip/Tooltip.jsx";
import {
  selectAllServices,
  selectTariffsInfo,
} from "../../redux/service/selectors.js";
import { getDaysLabel } from "../../utils/getDaysLabel.js";
import BalanceTopupModal from "../Modals/BalanceTopupModal/BalanceTopupModal.jsx";
import BalanceUsageModal from "../Modals/BalanceUsageModal/BalanceUsageModal.jsx";

export default function UserInfo() {
  const user = useSelector(selectUser);
  const services = useSelector(selectAllServices);
  const selectedServiceIdInState = useSelector(selectSelectedServiceId);
  const chatIsOpen = useSelector(selectIsChatOpen);
  const tariffsInfo = useSelector(selectTariffsInfo);

  const [modalIsOpen, setIsOpen] = useState(false);
  const [balanceTopupModal, setBalanceTopupModal] = useState(false);
  const [balanceUsageModalOpen, setBalanceUsageModalOpen] = useState(false);
  const [tariffInfo, setTariffInfo] = useState(null);
  const dispatch = useDispatch();

  const selectedService = services.find(
    service => service.id === selectedServiceIdInState
  );

  const {
    tooltipPosition,
    isTooltipVisible,
    tooltipText,
    activeId,
    handleMouseEnter,
    handleMouseMove,
    handleMouseLeave,
    handleClick,
  } = useTooltip();

  const openModal = () => {
    setIsOpen(true);
    dispatch(getPosts());
    dispatch(getTariffs());
  };

  const handleModalClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    setTariffInfo(tariffsInfo)
  }, [tariffsInfo])
  

  const avatarUrl = user?.avatar_url || defaultAvatar;
  const fullName =
    user?.name?.trim() ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
    "Ім'я користувача";

  const role = user?.role?.name || "Невідомо";
  const userId = user?.id || "Невідомо";

  const handleTariffClick = () => {
    openModal();
    handleClick();
  };

  const handleBalanceClick = () => {
    setBalanceUsageModalOpen(true);
    handleClick();
  };

  return (
    <div
      className={clsx(styles.userInfo, {
        [styles.userInfoWithChat]: chatIsOpen,
      })}
    >
      <div className={styles.avatar}>
        <img
          src={avatarUrl || defaultAvatar}
          alt={fullName}
          onError={e => {
            e.target.onerror = null;
            e.target.src = defaultAvatar;
          }}
        />
      </div>
      <h2
        className={clsx(styles.name, {
          [styles.hidden]: chatIsOpen,
          [styles.visible]: !chatIsOpen,
        })}
      >
        {fullName}
      </h2>

      <div
        className={clsx(styles.userDetails, {
          [styles.hidden]: chatIsOpen,
          [styles.visible]: !chatIsOpen,
        })}
      >
        <p>{role}</p>
        <p>ID: {userId}</p>
      </div>

      {/* Тарифний план */}
      <div
        className={clsx(styles.balance, {
          [styles.hidden]: chatIsOpen,
          [styles.visible]: !chatIsOpen,
        })}
      >
        <span className={styles.balanceText}>Тарифний план:</span>
        <button
          className={styles.balanceButton}
          onClick={handleTariffClick}
          onMouseEnter={e =>
            handleMouseEnter(e, "Змінити тарифний план", "tariff")
          }
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <span className={styles.num}>{tariffInfo?.tariff_name}</span>
          <FaChevronRight className={styles.icon} />
          {isTooltipVisible && activeId === "tariff" && (
            <Tooltip
              isVisible={isTooltipVisible}
              position={tooltipPosition}
              text={tooltipText}
            />
          )}
        </button>
      </div>

      {/* Термін дії */}
      <div
        className={clsx(styles.balance, {
          [styles.hidden]: chatIsOpen,
          [styles.visible]: !chatIsOpen,
        })}
      >
        <span className={styles.balanceText}>Термін дії:</span>
        <span className={styles.num}>
          {tariffInfo?.days_remaining}{" "}
          {getDaysLabel(tariffInfo?.days_remaining)}
        </span>
      </div>

      {/* Баланс */}
      <div
        className={clsx(styles.balance, {
          [styles.hidden]: chatIsOpen,
          [styles.visible]: !chatIsOpen,
        })}
      >
        <span className={styles.balanceText}>Баланс:</span>
        <button
          className={styles.balanceButton}
          onClick={handleBalanceClick}
          onMouseEnter={e => handleMouseEnter(e, "Деталі балансу", "balance")}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <span className={styles.num}>{selectedService?.balance}</span>
          <FaChevronRight className={styles.icon} />
          {isTooltipVisible && activeId === "balance" && (
            <Tooltip
              isVisible={isTooltipVisible}
              position={tooltipPosition}
              text={tooltipText}
            />
          )}
        </button>
      </div>

      {/* Одне модальне вікно для обох кнопок */}
      {modalIsOpen && (
        <Modal isOpen={modalIsOpen} onClose={handleModalClose}>
          <PaymentTopUpAccountModal onClose={handleModalClose} />
        </Modal>
      )}
      {balanceTopupModal && (
        <Modal
          isOpen={balanceTopupModal}
          onClose={() => setBalanceTopupModal(false)}
        >
          <BalanceTopupModal
            selectedService={selectedService}
            onClose={() => setBalanceTopupModal(false)}
          />
        </Modal>
      )}

      {balanceUsageModalOpen && (
        <Modal
          isOpen={balanceUsageModalOpen}
          onClose={() => setBalanceUsageModalOpen(false)}
        >
          <BalanceUsageModal
            selectedService={selectedService}
            onClose={() => setBalanceUsageModalOpen(false)}
            onOpenTopup={() => {
              setBalanceUsageModalOpen(false);
              setBalanceTopupModal(true);
            }}
          />
        </Modal>
      )}
    </div>
  );
}
